// Section 2 — "Processing order & household budget"
// (explains-page-contract.json s2_order_budget): the order facet table
// with the ranking rule stated once in the caption, the household
// budget by asset class, and the E2-gated gains-at-risk detail.
import { Card, Table } from "react-bootstrap";
import { formatCurrency, formatPercent } from "../../../../lib/numbers";
import type { SoftSectionContext } from "./SoftCompletionContext";
import { HBarChart } from "./charts";
import {
  hasGainsAtRiskEnrichment,
  num,
  sortedByValueDesc,
} from "./softCompletionLib";

const RANKING_RULE =
  "Processing order — own-model accounts first, smallest balance first; " +
  "taxable accounts with gains at risk move up, largest first when tied; " +
  "household-model accounts follow, taxable before tax-exempt";

// Translate the engine's processing_order_reason code to advisor copy.
// "default" (no re-rank) renders blank; "3b:{ac}:{dollars}" is the
// taxable re-rank on the contested class; unknown codes pass through.
function reasonLabel(reason: string | null | undefined): string {
  if (!reason || reason === "default") return "";
  const reranked = /^3b:(.+):(-?\d+)$/.exec(reason);
  if (reranked) {
    const dollars = Number(reranked[2]);
    return dollars > 0
      ? `moved up — ${formatCurrency(dollars)} gains at risk on ${reranked[1]}`
      : `moved up — gains at risk on ${reranked[1]}`;
  }
  return reason;
}

export default function ProcessingOrderSection({
  ctx,
}: {
  ctx: SoftSectionContext;
}) {
  const { completion, accountsById, resolveAccountName } = ctx;
  const aum = num(completion.household_aum);
  const classifications = [...(completion.account_classifications ?? [])].sort(
    (a, b) => a.processing_order - b.processing_order,
  );
  const showGainsDetail = hasGainsAtRiskEnrichment(completion);
  const gainsRows = classifications.filter((c) => c.contested_ac != null);

  return (
    <Card className="mb-3">
      <Card.Header>Processing order &amp; household budget</Card.Header>
      <Card.Body>
        <Table size="sm" responsive className="mb-0">
          <caption className="text-muted small">{RANKING_RULE}</caption>
          <thead>
            <tr>
              <th className="text-end">#</th>
              <th>Account</th>
              <th>Model</th>
              <th>Taxable</th>
              <th className="text-end">Balance</th>
              <th className="text-end">Gains at risk</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {classifications.map((c) => {
              const account = accountsById.get(c.account_id);
              const isOwnModel = c.processing_group === "model";
              return (
                <tr key={c.account_id}>
                  <td className="text-end">{c.processing_order + 1}</td>
                  <td>{resolveAccountName(c.account_id)}</td>
                  <td>
                    {isOwnModel
                      ? (account?.model?.name ?? `Model ${c.model_id ?? "—"}`)
                      : "Household model"}
                  </td>
                  <td>{account?.is_taxable ? "Yes" : "No"}</td>
                  <td className="text-end">{formatCurrency(num(c.balance))}</td>
                  <td className="text-end">
                    {c.gains_at_risk_dollars != null
                      ? formatCurrency(num(c.gains_at_risk_dollars))
                      : "—"}
                  </td>
                  <td>{reasonLabel(c.processing_order_reason)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        <HBarChart
          title="Household model by asset class — weight × AUM"
          rows={sortedByValueDesc(
            completion.household_target_by_asset_class,
          ).map(([ac, dollars]) => ({
            label: ac,
            segments: [{ value: dollars, color: ctx.acColor(ac) }],
            note: `${formatCurrency(dollars)} · ${aum > 0 ? formatPercent(dollars / aum, 1) : "—"}`,
          }))}
        />
        <p className="text-muted small mb-0">
          The household model at asset-class level, in dollars. Accounts are fit
          in the order above; each account&apos;s allocation counts against
          these class totals, and a later account&apos;s allocation in a class
          is limited to what remains.
        </p>

        {showGainsDetail && gainsRows.length > 0 && (
          <Table size="sm" responsive className="mt-3 mb-0">
            <caption className="text-muted small">
              Gains at risk — taxable accounts holding the contested class above
              their model target get first claim on that class&apos;s budget
            </caption>
            <thead>
              <tr>
                <th>Account</th>
                <th>Contested class</th>
                <th className="text-end">Current $</th>
                <th className="text-end">Model target $</th>
                <th className="text-end">Overweight to model</th>
              </tr>
            </thead>
            <tbody>
              {gainsRows.map((c) => (
                <tr key={c.account_id}>
                  <td>{resolveAccountName(c.account_id)}</td>
                  <td>{c.contested_ac}</td>
                  <td className="text-end">
                    {c.held_dollars != null
                      ? formatCurrency(num(c.held_dollars))
                      : "—"}
                  </td>
                  <td className="text-end">
                    {c.model_target_dollars != null
                      ? formatCurrency(num(c.model_target_dollars))
                      : "—"}
                  </td>
                  <td className="text-end">
                    {c.gains_at_risk_dollars != null
                      ? formatCurrency(num(c.gains_at_risk_dollars))
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}
